import React, { useEffect, useRef, useState } from "react";
import "./Login.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UseAuthService } from "@shared/hooks/useAuthService";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "@shared/api/api";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

type Tenant = {
  logoUrl: string; // 로고
  subtitleMain: string; // 강조 문구
  subtitle: string; // 서브 문구
  subtitleColor: string; // 문구 색상
  loginButtonColor: string; // 로그인 버튼 색
};

// 기본 로고
const FALLBACK_LOGO = "/images/logo_snotes.svg";

const DEFAULT_TENANT: Tenant = {
  logoUrl: FALLBACK_LOGO,
  subtitleMain: "기업의 모든 업무를 하나로 연결합니다.",
  subtitle: "생산·물류·영업·회계 정보를 통합 관리하는 스마트 ERP 솔루션",
  subtitleColor: "#5a648a",
  loginButtonColor: "#0b0c69",
};

export default function Login() {
  const userIdRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const isSubmitRef = useRef(false);

  const [language, setLanguage] = useState<string>("KO");
  const [userId, setUserID] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberId, setRememberId] = useState<boolean>(true);

  // 업체별 설정 (기본값으로 시작, 설정값이 있으면 덮어쓴다)
  const [tenant, setTenant] = useState<Tenant>(DEFAULT_TENANT);

  const currentYear = new Date().getFullYear();

  const [searchParams] = useSearchParams();

  const tenantUrl = searchParams.get("tenant") || "DEFAULT";

  const { login } = UseAuthService();

  useEffect(() => {
    document.title = "SmartNotes";
  }, []);

  useEffect(() => {
    const setSavedId = async () => {
      const savedId = localStorage.getItem("savedId");
      if (savedId) {
        setUserID(savedId);
        setRememberId(true);
      }
    };
    setSavedId();
  }, []);

  useEffect(() => {
    const code = (tenantUrl || "DEFAULT").toLowerCase();
    const candidates = [
      { url: `/tenant/${code}/login-design.json`, logoBase: `/tenant/${code}` },
      { url: `/login-design.default.json`, logoBase: "" },
    ];

    const applyConfig = (logoBase: string, cfg: any) => {
      setTenant({
        subtitleMain: cfg.subtitleMain ?? DEFAULT_TENANT.subtitleMain,
        subtitle: cfg.subtitle ?? DEFAULT_TENANT.subtitle,
        subtitleColor: cfg.subtitleColor ?? DEFAULT_TENANT.subtitleColor,
        loginButtonColor:
          cfg.loginButtonColor ?? DEFAULT_TENANT.loginButtonColor,
        logoUrl: cfg.logo ? `${logoBase}/${cfg.logo}` : FALLBACK_LOGO,
      });
      if (cfg.tenantName) {
        document.title = `SmartNotes - ${cfg.tenantName}`;
      }
    };

    (async () => {
      for (const c of candidates) {
        try {
          const res = await fetch(c.url, { cache: "no-cache" });
          if (res.ok) {
            applyConfig(c.logoBase, await res.json());
            return;
          }
        } catch (e) {}
      }
      setTenant(DEFAULT_TENANT);
    })();
  }, [tenantUrl]);

  useEffect(() => {
    if (!tenantUrl) return;

    (async () => {
      try {
        const response = await apiFetch("/tenant/info", {
          method: "GET",
          headers: {
            "X-TENANT-ID": tenantUrl,
          },
        });

        if (response.code === 200) {
          if (
            window.location.pathname === "/login" &&
            window.location.search &&
            response.data.tenantId === "DEFAULT"
          ) {
            window.location.replace("/login");
          } else {
            if (tenantUrl !== "DEFAULT") {
              document.title = `SmartNotes - ${response.data.tenantName}`;
            }
          }
        }
      } catch (error) {
        // 호출 실패로는 tenant 파라미터를 떼지 않는다. 파라미터 제거는 BE가 200으로
        // "모르는 테넌트(DEFAULT)"라고 답했을 때만 유효한 처리다 — 일시적인 통신
        // 오류에 테넌트가 조용히 풀리고 전체 리로드까지 발생한다.
        // 브랜딩은 위쪽 effect의 기본값이 처리한다.
        console.error("tenant/info 조회 실패", error);
      }
    })();
  }, [tenantUrl]);

  const handleLogin = async () => {
    if (isSubmitRef.current) return;
    isSubmitRef.current = true;

    try {
      if (!userId || !password) {
        alert(" ID와 Password를 입력해주세요.");
        return;
      }

      if (rememberId && userId) {
        localStorage.setItem("savedId", userId);
      } else {
        localStorage.removeItem("savedId");
      }

      const response = await login(userId, password, tenantUrl);

      // 로그인에 성공한 테넌트만 저장한다.
      // (실패한 값까지 저장하면 오타로 들어온 테넌트가 계속 따라다닌다)
      localStorage.setItem("savedTenant", tenantUrl);

      if (response.error && response.error.errorCode === "LICENSE_WARNING") {
        toast.warning(response.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      isSubmitRef.current = false;
    }
  };

  return (
    <div
      className="login-container"
      style={{ backgroundImage: "url(/images/login_ai_bg.png)" }}
    >
      {/* 로그인 영역 */}
      <div className="left-section">
        <div className="left-main">
          {/* 로고 */}
          <div className="brand-logo">
            <img
              src={tenant.logoUrl}
              alt="logo"
              className="brand-logo-img"
              onError={(e) => {
                const el = e.currentTarget as HTMLImageElement;
                if (el.src.indexOf(FALLBACK_LOGO) === -1)
                  el.src = FALLBACK_LOGO;
              }}
            />
          </div>

          {/* 서브 문구 */}
          <div
            className="brand-subtitle"
            style={{ color: tenant.subtitleColor }}
          >
            <p className="brand-subtitle-main">{tenant.subtitleMain}</p>
            <p className="brand-subtitle-desc">{tenant.subtitle}</p>
          </div>

          {/* 로그인 폼 */}
          <div className="login-box" style={{ fontFamily: "nexonFont" }}>
            <div className="login-box-header">
              <h2 className="login-box-title">로그인</h2>
              <div className="login-lang">
                <span className="login-lang-label">언어</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  <option value={"origin"}>표준</option>
                  <option value={"KO"}>한국어</option>
                  <option value={"EN"}>ENGLISH</option>
                  <option value={"JP"}>日本語</option>
                  <option value={"CN"}>中文</option>
                  <option value={"VI"}>Tiếng Việt</option>
                </select>
              </div>
            </div>

            {/* 아이디 */}
            <label className="field-label">아이디</label>
            <div className="input-group">
              <input
                value={userId}
                type="text"
                placeholder="아이디를 입력하세요"
                ref={userIdRef}
                onChange={(e) => setUserID(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key == "Enter") {
                    passwordRef.current?.focus();
                  }
                }}
              />
            </div>

            {/* 비밀번호 */}
            <label className="field-label">비밀번호</label>
            <div className="input-group">
              <input
                value={password}
                type={showPassword ? "text" : "password"}
                placeholder="비밀번호를 입력하세요"
                ref={passwordRef}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key == "Enter") {
                    handleLogin();
                  }
                }}
              />
              <button
                type="button"
                className="pw-toggle"
                tabIndex={-1}
                aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            {/* 아이디 저장 */}
            <div className="form-options">
              <label className="remember-id">
                <input
                  type="checkbox"
                  checked={rememberId}
                  onChange={(e) => setRememberId(e.target.checked)}
                />
                아이디 저장
              </label>
            </div>

            {/* 로그인 버튼 */}
            <button
              className="login-button"
              style={{ backgroundColor: tenant.loginButtonColor }}
              onClick={handleLogin}
            >
              로그인
            </button>
          </div>
        </div>

        {/* 카피라이트 */}
        <div className="login-footer">
          <p className="login-copyright">
            © {currentYear} G2SYSNET. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
